export const PWA_INSTALL_DISMISSED_AT_KEY = 'iok:pwa-install-dismissed-at';
export const PWA_INSTALL_PROMPT_SHOWN_KEY = 'iok:pwa-install-prompt-shown';

export const PWA_INSTALL_DISMISSAL_COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000;

type StorageProvider = () => Storage;

const safeStorageRead = (getStorage: StorageProvider, key: string) => {
  try {
    return getStorage().getItem(key);
  } catch {
    return null;
  }
};

const safeStorageWrite = (
  getStorage: StorageProvider,
  key: string,
  value: string
) => {
  try {
    getStorage().setItem(key, value);
  } catch {
    // Storage can be unavailable in private or hardened browsing modes.
  }
};

export const isRunningAsInstalledPwa = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(
    window.matchMedia?.('(display-mode: standalone)').matches ||
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

export const isIosDevice = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const { navigator } = window;
  const isClassicIos = /iPad|iPhone|iPod/i.test(navigator.userAgent);
  const isModernIpad =
    navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  return isClassicIos || isModernIpad;
};

export const wasPwaInstallPromptShownThisSession = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return (
    safeStorageRead(
      () => window.sessionStorage,
      PWA_INSTALL_PROMPT_SHOWN_KEY
    ) === 'true'
  );
};

export const markPwaInstallPromptShown = () => {
  if (typeof window === 'undefined') {
    return;
  }

  safeStorageWrite(
    () => window.sessionStorage,
    PWA_INSTALL_PROMPT_SHOWN_KEY,
    'true'
  );
};

export const dismissPwaInstallPrompt = (dismissedAt = Date.now()) => {
  if (typeof window === 'undefined') {
    return;
  }

  safeStorageWrite(
    () => window.localStorage,
    PWA_INSTALL_DISMISSED_AT_KEY,
    String(dismissedAt)
  );
};

export const isPwaInstallPromptInCooldown = (now = Date.now()) => {
  if (typeof window === 'undefined') {
    return false;
  }

  const rawDismissedAt = safeStorageRead(
    () => window.localStorage,
    PWA_INSTALL_DISMISSED_AT_KEY
  );
  const dismissedAt = Number(rawDismissedAt);

  if (!rawDismissedAt || !Number.isFinite(dismissedAt)) {
    return false;
  }

  return now - dismissedAt < PWA_INSTALL_DISMISSAL_COOLDOWN_MS;
};

